import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../../i18n';
import type { UtilBox } from '../../lib/fleetUtilization';
import { memoryUsedPercent } from '../../lib/machine-bars';
import type { MovePreview, MovePreviewTarget } from '../../lib/move-calls';
import type { MoveSubject } from '../../lib/move-items';
import { MoveIcon } from './MoveIcon';
import { MoveProjRow } from './MoveProjRow';

// The panel a hovered card opens during a drag (#6771, prototype dropHTML `:1943-1953`,
// projRow `:1939`): "Move to <m>" with what moves, a CPU and a Memory row "a% → b%" with the
// added share as a bar segment (accent below 70%, amber at 70% or more, red above 90%; the
// after value bold and coloured at 70% or more), the moves this minute, and the source's CPU
// after. A refusing card reads "Can’t move to <m>" and the sentence.
//
// EVERY NUMBER IS THE SERVER'S PREVIEW (#6768): `cpu` is busy percent; `memory` is FREE BYTES,
// shown used as `100 × (specs.mem_bytes − free) / specs.mem_bytes`. A null projection omits the
// row (its "before" is unknown too); a null `specs` omits the Memory row. Never an estimate.

export function DropPanel(props: {
  readonly subject: MoveSubject;
  readonly target: UtilBox;
  readonly source: UtilBox | undefined;
  readonly answer: MovePreviewTarget;
  readonly preview: MovePreview;
  readonly why: string | null;
  /** Its top inside the card, px. */
  readonly top: number;
  /** The panel element, measured for its placement. */
  readonly onMeasure?: (el: HTMLDivElement) => void;
}): JSX.Element {
  const name = () => props.target.display_name;
  /** CPU busy percent: the preview's projection, else the machine's own reading, before only. */
  const cpu = (): { before: number; after: number | null } | null => {
    const p = props.answer.cpu;
    if (p !== null) return p;
    const ratio = props.target.verdict.signals.cpu.ratio;
    return ratio === null ? null : { before: ratio * 100, after: null };
  };
  /** Memory used percent of `specs.mem_bytes`, from free bytes; no specs, no row. */
  const memory = (): { before: number; after: number | null } | null => {
    const total = props.target.specs?.mem_bytes ?? null;
    if (total === null) return null;
    const m = props.answer.memory;
    if (m !== null)
      return {
        before: memoryUsedPercent(total, m.before),
        after: memoryUsedPercent(total, m.after),
      };
    const free = props.target.stats?.freeMemBytes ?? null;
    return free === null ? null : { before: memoryUsedPercent(total, free), after: null };
  };
  const sub = (): string => {
    const s = props.subject;
    if (s.item.kind !== 'epic')
      return t('fleet.move.panel_sub', { label: s.label, title: s.title });
    return tPlural('fleet.move.panel_sub_epic', s.jobs.length, {
      title: s.title,
      count: s.jobs.length,
    });
  };
  const leftAfter = (): number =>
    Math.max(0, Math.min(props.answer.left_after, props.preview.source.left - props.preview.cost));
  return (
    <div
      ref={(el) => props.onMeasure?.(el)}
      class="move-drop__panel"
      classList={{ 'move-drop__panel--no': props.why !== null }}
      style={{ top: `${props.top}px` }}
    >
      <Show
        when={props.why === null}
        fallback={
          <>
            <p class="move-drop__head">
              <b>
                <MoveIcon glyph="block" />
                {t('fleet.move.panel_no', { name: name() })}
              </b>
            </p>
            <p class="move-drop__why">{props.why}</p>
          </>
        }
      >
        <p class="move-drop__head">
          <b>{t('fleet.move.panel_to', { name: name() })}</b>
          <span>{sub()}</span>
        </p>
        <Show when={cpu()}>
          {(p) => (
            <MoveProjRow label={t('fleet.move.panel_cpu')} before={p().before} after={p().after} />
          )}
        </Show>
        <Show when={memory()}>
          {(p) => (
            <MoveProjRow
              label={t('fleet.move.panel_memory')}
              before={p().before}
              after={p().after}
            />
          )}
        </Show>
        <p class="move-drop__row">
          <span>{t('fleet.move.panel_cost')}</span>
          <span class="move-drop__mono">
            {t('fleet.move.panel_cost_value', { cost: props.preview.cost, left: leftAfter() })}
          </span>
        </p>
        <Show when={props.preview.source.cpu}>
          {(p) => (
            <p class="move-drop__row move-drop__row--from">
              <span>{t('fleet.move.panel_from', { name: props.source?.display_name ?? '' })}</span>
              <span class="move-drop__mono">
                {t('fleet.move.panel_from_before', { value: Math.round(p().before) })}{' '}
                <b>{t('fleet.machines.percent', { value: Math.round(p().after) })}</b>
              </span>
            </p>
          )}
        </Show>
      </Show>
    </div>
  );
}
